import { useState } from "react"
import { Link, Navigate, useSearchParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowRight, Loader2 } from "lucide-react"
import { usePostHog } from "@posthog/react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout } from "@/components/auth-layout"
import {
  AuthCard,
  AuthHeading,
  LogoMark,
} from "@/components/auth-card"
import { AuthField, AuthErrorBanner } from "@/components/auth-field"
import { AuthSuccess } from "@/components/auth-success"
import { AuthMaintenance } from "@/components/auth-maintenance"
import { useServerInfo } from "@/hooks/use-server-info"
import { useSession } from "@/hooks/use-session"

const schema = z.object({
  email: z.string().email("That doesn't look like a valid email"),
})

type FormValues = z.infer<typeof schema>

export function LoginPage() {
  const { info, loading } = useServerInfo()
  const session = useSession()
  // Wait until we know whether maintenance is on; mounting <LoginForm /> first
  // and then unmounting it would shift hook count between renders (React #300).
  if (loading || session.status === "loading") return null
  // Already signed in: skip the login form entirely.
  if (session.status === "authenticated") return <Navigate to="/" replace />
  if (info?.maintenance) return <AuthMaintenance />
  return <LoginForm />
}

function LoginForm() {
  const posthog = usePostHog()
  const [params] = useSearchParams()
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [noAccountEmail, setNoAccountEmail] = useState<string | null>(null)

  // Derived from the URL each render; setting state in useEffect to mirror
  // this would cascade renders (react-hooks/set-state-in-effect).
  const urlError = params.get("error") === "expired"
    ? "That sign-in link expired or was already used. Send yourself a new one."
    : null
  const formError = submitError ?? urlError

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  })

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null)
    try {
      const res = await fetch("/api/v1/saas/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      })
      const body = (await res.json()) as { ok?: boolean; error?: string }

      if (res.status === 404) {
        setNoAccountEmail(values.email)
        return
      }
      if (!res.ok) {
        setSubmitError(humanizeLoginError(res.status, body.error))
        return
      }
      posthog?.capture("login_requested")
      setSubmittedEmail(values.email)
      setSubmitted(true)
    } catch {
      setSubmitError("Couldn't reach the server. Try again in a moment.")
    }
  }

  const handleResend = () => {
    setSubmitted(false)
    setSubmitError(null)
    form.reset({ email: submittedEmail })
  }

  if (noAccountEmail) {
    return (
      <AuthLayout>
        <LogoMark />
        <AuthCard>
          <div className="space-y-6">
            <AuthHeading
              eyebrow="No account found"
              title="Let's get you set up"
              description={`We couldn't find a HiveHook account for ${noAccountEmail}. Create one to get started.`}
            />
            <Link
              to={`/signup?email=${encodeURIComponent(noAccountEmail)}`}
              className="group flex w-full items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30"
            >
              Create an account
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button
              type="button"
              onClick={() => {
                setNoAccountEmail(null)
                form.reset({ email: "" })
              }}
              className="w-full text-center text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              Try a different email
            </button>
          </div>
        </AuthCard>
      </AuthLayout>
    )
  }

  if (submitted) {
    return (
      <AuthLayout>
        <LogoMark />
        <AuthCard>
          <AuthSuccess
            email={submittedEmail}
            purpose="login"
            onResend={handleResend}
          />
        </AuthCard>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        <div className="space-y-7">
          <AuthHeading
            eyebrow="Welcome back"
            title="Sign in to Hivehook"
            description="Drop in your email and we'll send a one-time link. No passwords, no friction."
          />

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <AuthField
              label="Email"
              error={form.formState.errors.email?.message}
            >
              <Input
                {...form.register("email")}
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                aria-invalid={!!form.formState.errors.email}
              />
            </AuthField>

            {formError && <AuthErrorBanner message={formError} />}

            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="group w-full bg-rose-600 text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30 disabled:bg-rose-600/60"
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending link
                </>
              ) : (
                <>
                  Send sign-in link
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="border-t border-border/40 pt-5 text-center text-sm text-muted-foreground">
            New here?{" "}
            <Link
              to="/signup"
              className="font-medium text-foreground underline decoration-rose-400/40 decoration-2 underline-offset-4 transition-colors hover:text-rose-400 hover:decoration-rose-400"
            >
              Start a trial
            </Link>
          </div>
        </div>
      </AuthCard>

      <p className="mt-6 text-center text-2xs uppercase tracking-[0.18em] text-muted-foreground/50">
        Magic links are easier than passwords
      </p>
    </AuthLayout>
  )
}

function humanizeLoginError(status: number, raw?: string): string {
  switch (status) {
    case 400:
      return raw || "That email didn't look right. Try again."
    case 404:
      return "We couldn't find an account with that email."
    case 429:
      return "Too many sign-in attempts for that email. Give it a minute."
    case 503:
      return "Email isn't configured on this server. Reach out so we can fix it."
    default:
      return raw || "Something went wrong. Try again in a moment."
  }
}
